// ---------------------------------------------------------------------------
// Audit Log - page constants, filter options, action styles and labels
// ---------------------------------------------------------------------------

import {
  Activity,
  Eye,
  FileDown,
  LogIn,
  Pencil,
  PlusCircle,
  RefreshCw,
  Trash2,
  type LucideIcon,
} from "lucide-react";

export const PAGE_SIZE = 20;
export const AUTO_REFRESH_INTERVAL = 30_000;
export const DEBOUNCE_MS = 400;

export const ACTION_TYPE_OPTIONS: readonly {
  readonly value: string;
  readonly label: string;
}[] = [
  { value: "", label: "Toutes les actions" },
  { value: "create", label: "Creation" },
  { value: "update", label: "Modification" },
  { value: "delete", label: "Suppression" },
  { value: "status_change", label: "Changement de statut" },
  { value: "view", label: "Consultation" },
  { value: "login", label: "Connexion" },
  { value: "export", label: "Export" },
];

export const RESOURCE_TYPE_OPTIONS: readonly {
  readonly value: string;
  readonly label: string;
}[] = [
  { value: "", label: "Toutes les ressources" },
  { value: "orders", label: "Commandes" },
  { value: "customers", label: "Clients" },
  { value: "products", label: "Produits" },
  { value: "payments", label: "Paiements" },
  { value: "admin_users", label: "Utilisateurs" },
  { value: "settings", label: "Parametres" },
  { value: "deliveries", label: "Livraisons" },
];

// ---------------------------------------------------------------------------
// Action icon + color mapping
// ---------------------------------------------------------------------------

export interface ActionStyle {
  readonly icon: LucideIcon;
  readonly bgColor: string;
  readonly iconColor: string;
  readonly badgeBg: string;
  readonly badgeText: string;
}

export const ACTION_STYLES: Record<string, ActionStyle> = {
  create: {
    icon: PlusCircle, bgColor: "bg-emerald-100", iconColor: "text-emerald-600",
    badgeBg: "bg-emerald-50 border-emerald-200", badgeText: "text-emerald-700",
  },
  update: {
    icon: Pencil, bgColor: "bg-blue-100", iconColor: "text-blue-600",
    badgeBg: "bg-blue-50 border-blue-200", badgeText: "text-blue-700",
  },
  delete: {
    icon: Trash2, bgColor: "bg-red-100", iconColor: "text-red-600",
    badgeBg: "bg-red-50 border-red-200", badgeText: "text-red-700",
  },
  status_change: {
    icon: RefreshCw, bgColor: "bg-amber-100", iconColor: "text-amber-600",
    badgeBg: "bg-amber-50 border-amber-200", badgeText: "text-amber-700",
  },
  view: {
    icon: Eye, bgColor: "bg-stone-100", iconColor: "text-stone-500",
    badgeBg: "bg-stone-50 border-stone-200", badgeText: "text-stone-600",
  },
  login: {
    icon: LogIn, bgColor: "bg-violet-100", iconColor: "text-violet-600",
    badgeBg: "bg-violet-50 border-violet-200", badgeText: "text-violet-700",
  },
  export: {
    icon: FileDown, bgColor: "bg-stone-100", iconColor: "text-stone-600",
    badgeBg: "bg-stone-50 border-stone-200", badgeText: "text-stone-600",
  },
};

export const DEFAULT_ACTION_STYLE: ActionStyle = {
  icon: Activity, bgColor: "bg-stone-100", iconColor: "text-stone-500",
  badgeBg: "bg-stone-50 border-stone-200", badgeText: "text-stone-600",
};

// ---------------------------------------------------------------------------
// Action & resource labels (French)
// ---------------------------------------------------------------------------

export const ACTION_LABELS: Record<string, string> = {
  create: "Creation",
  update: "Modification",
  delete: "Suppression",
  status_change: "Changement de statut",
  view: "Consultation",
  login: "Connexion",
  export: "Export",
};

export const RESOURCE_LABELS: Record<string, string> = {
  orders: "Commande",
  customers: "Client",
  products: "Produit",
  payments: "Paiement",
  admin_users: "Utilisateur",
  settings: "Parametres",
  deliveries: "Livraison",
};
